
import BookingTime from '@/core/components/BookingTime.vue';
import ConfirmationDialog from '@/core/components/ConfirmationDialog.vue';
import MainButton from '@/core/components/MainButton.vue';
import OtherBookings from '@/core/components/OtherBookings.vue';
import Spinner from '@/core/components/Spinner.vue';
import TabSelection from '@/core/components/TabSelection.vue';
import { Booking, CreateBooking, Machine, PostBooking, Project } from '@/models/bookings';
import { Driver, User } from '@/models/users';
import * as TelliqService from '@/services/telliq.service';
import { GETTERS } from '@/store/getters';
import { MUTATIONS } from '@/store/mutations';
import { BookingTimeDisplay, CREATE_BOOKING_DATE_FORMAT, emitter } from '@/utils/constants';
import {
  bookingEndDateReconstruction,
  bookingStartDateReconstruction,
  confirmationDialogText,
  tabNames,
} from '@/utils/functions';
import moment from 'moment';
import Vue from 'vue';
import BookingProject from '../components/BookingProject.vue';
import BookingMixin from '../mixins/BookingDateMixin';

export default BookingMixin.extend({
  data() {
    return {
      tab_names: tabNames(),
      loading: false,
      updating: false,
      valid: false,
      show_dialog: false,
      confirmation_dialog_text: { title: '', text: '' },
      booking_id: 0,
      tab_selected: 0,
      isDeleting: false,
      time_display: BookingTimeDisplay.BOTH,
      rules: {
        description: [
          (v: string) =>
            this.$data.tab_selected === 1 ||
            this.$data.created_booking?.projectId >= 0 ||
            !!v ||
            this.$t('core.rules.required'),
          (v: string) => v.length <= 500 || this.$t('core.rules.max_500_characters'),
        ],
      },
      machine: undefined as Machine | undefined,
      projects: [] as Project[],
      project_description: undefined as string | undefined,
      drivers: [] as Driver[],
      previous_bookings: [] as Booking[],
      next_bookings: [] as Booking[],
      booking: {} as Booking,
      created_booking: {} as CreateBooking,
    };
  },

  components: {
    Spinner,
    BookingTime,
    MainButton,
    TabSelection,
    BookingProject,
    OtherBookings,
    ConfirmationDialog,
  },

  async created() {
    this.loading = true;
    this.booking_id = +this.$route.params['id'];
    try {
      const [booking, machines, drivers, projects] = await Promise.all([
        TelliqService.getBookingById(this.booking_id),
        TelliqService.getMachines(),
        TelliqService.getDrivers(),
        this.loadProjects(),
      ]);
      this.machine = machines.find((machine) => machine.id === booking.machine.id);
      this.tab_selected = booking.isPrivateUse ? 1 : 0;

      this.booking = { ...booking };
      const title = this.canBeCheckedOut
        ? this.$t('bookings.headers.active_booking')
        : this.$t('bookings.headers.future_booking');
      this.$store.commit(MUTATIONS.SET_HEADER, {
        title,
        subtitle: `${this.$t('bookings.labels.car_name')} · ${this.machine?.name}`,
      });

      const { previous, next } = await this.getBeforeAndAfterBookings(booking, [booking.machine.id]);
      this.previous_bookings = previous;
      this.next_bookings = next;

      this.drivers = drivers;
      this.projects = projects;

      if (this.$route.query.editing) {
        this.created_booking = this.$store.getters[GETTERS.GET_CREATE_BOOKING];
      } else {
        this.created_booking = {
          startDate: moment(booking.startDate).format(CREATE_BOOKING_DATE_FORMAT),
          startTime: moment(booking.startDate).format('HH:mm:ss.SSS'),
          endDate: moment(booking.endDate).format(CREATE_BOOKING_DATE_FORMAT),
          endTime: moment(booking.endDate).format('HH:mm:ss.SSS'),
          machine: booking.machine,
          projectId: booking.isPrivateUse ? undefined : booking.project?.id,
          canBePrivate: this.machine?.isPrivateBookingEnabled,
          intention: booking.intention ?? '',
          isPrivateUse: booking.isPrivateUse,
          driverHasDriverLicense: booking.driverHasDriverLicense,
        };
        this.$store.commit(MUTATIONS.SET_CREATE_BOOKING, this.created_booking);
        this.$store.commit(MUTATIONS.SET_EDIT_BOOKING, this.booking_id);
      }

      // if redirect comes from check-in, present checkout popup
      if (this.$route.query.checkout === 'confirm') {
        this.onCheckOutClick();
      }
    } catch (e) {
      console.log(e);
      emitter.emit('notify', {
        text: this.$t('core.errors.something_went_wrong'),
        color: 'error',
      });
      this.$router.push('/');
    } finally {
      this.loading = false;
    }
  },

  computed: {
    canBeCanceled(): boolean {
      return moment().utcOffset(0, true).isBefore(this.booking.startDate);
    },

    canBeCheckedOut(): boolean {
      return moment().utcOffset(0, true).isBefore(this.booking.endDate);
    },
  },

  methods: {
    async loadProjects(): Promise<Project[]> {
      try {
        const user: User = this.$store.getters[GETTERS.GET_USER];
        const user_info = await TelliqService.getUserInfo(user.id);
        const projects = await TelliqService.getProjects(user_info.companyId);

        return projects.filter((project) => {
          if (project.active === false) return false;
          if (project.startUtcTime && moment(project.startUtcTime, 'YYYY-MM-DD HH:mm:ss').isAfter()) return false;
          if (project.endUtcTime && moment(project.endUtcTime, 'YYYY-MM-DD HH:mm:ss').isBefore()) return false;
          // Project type 1 is private.
          if (project.projectType === 1) return false;
          return true;
        });
      } catch (e) {
        emitter.emit('notify', {
          text: this.$t('core.errors.something_went_wrong'),
          color: 'error',
        });
      }
      return [];
    },

    async saveChanges(): Promise<void> {
      this.updating = true;
      const update_booking: PostBooking = {
        startDate: bookingStartDateReconstruction(this.created_booking),
        endDate: bookingEndDateReconstruction(this.created_booking),
        machineId: this.created_booking.machine?.id,
        projectId: this.created_booking.projectId,
        isPrivateUse: this.tab_selected === 1,
        intention: this.created_booking.intention,
        driverHasDriverLicense: this.created_booking.driverHasDriverLicense,
      };
      if (update_booking.projectId === null || update_booking.isPrivateUse) {
        delete update_booking.projectId;
      }
      try {
        await TelliqService.updateBooking(this.booking_id, update_booking);
        emitter.emit('notify', {
          text: this.$t('bookings.snackbars.booking_update_success'),
          color: 'success',
        });
        this.$router.push('/');
      } catch (e) {
        emitter.emit('notify', {
          text: this.$t('core.errors.something_went_wrong'),
          color: 'error',
        });
      } finally {
        this.updating = false;
      }
    },

    onTabChanged(tab_index: number): void {
      this.tab_selected = tab_index;
      this.created_booking.isPrivateUse = tab_index === 1;
      (this.$refs.form as Vue & { validate: () => boolean }).validate();
      this.$store.commit(MUTATIONS.SET_CREATE_BOOKING, this.created_booking);
    },

    onProjectChanged(project_id: number): void {
      this.created_booking.projectId = project_id;
      this.project_description = this.projects.find((project) => project.projectId === project_id)?.description;
      (this.$refs.form as Vue & { validate: () => boolean }).validate();
    },

    onCancelClick(): void {
      this.show_dialog = true;
      this.isDeleting = true;
      this.confirmation_dialog_text = confirmationDialogText().delete;
    },

    onCheckOutClick(): void {
      this.show_dialog = true;
      this.isDeleting = false;
      this.confirmation_dialog_text = confirmationDialogText().check_out;
    },

    async onDialogResponse(response: boolean): Promise<void> {
      if (response) {
        this.updating = true;
        if (this.isDeleting) {
          await this.deleteBooking();
        } else {
          await this.checkOutBooking();
        }
        this.updating = false;
      }
      this.show_dialog = false;
    },

    async deleteBooking(): Promise<void> {
      try {
        await TelliqService.deleteBooking(this.booking.id);
        await this.$router.push('/');
      } catch (e) {
        emitter.emit('notify', {
          text: this.$t('core.errors.something_went_wrong'),
          color: 'error',
        });
      }
    },

    async checkOutBooking(): Promise<void> {
      try {
        await TelliqService.stopBooking(this.booking_id, this.booking);
        await this.$router.push('/');
      } catch (e) {
        emitter.emit('notify', {
          text: this.$t('core.errors.something_went_wrong'),
          color: 'error',
        });
      }
    },
  },
});
